import { supabase } from '@/lib/supabase';
import type {
  Supplier,
  SupplierListItem,
  CreateSupplierInput,
  UpdateSupplierInput,
} from '../types';
import { mapSupplierFromDb, mapSupplierListItemFromDb } from '../types';

/**
 * Fetch all suppliers for a business using the v_suppliers view
 */
export async function fetchSuppliers(businessId: string): Promise<SupplierListItem[]> {
  const { data, error } = await supabase
    .from('v_suppliers')
    .select('*')
    .eq('business_id', businessId)
    .order('name', { ascending: true });

  if (error) {
    console.error('[suppliersApi] Error fetching suppliers:', error);
    throw new Error(error.message);
  }

  return (data || []).map(mapSupplierListItemFromDb);
}

/**
 * Fetch a single supplier by ID using the v_suppliers view
 */
export async function fetchSupplierById(supplierId: string): Promise<SupplierListItem | null> {
  const { data, error } = await supabase
    .from('v_suppliers')
    .select('*')
    .eq('id', supplierId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    console.error('[suppliersApi] Error fetching supplier:', error);
    throw new Error(error.message);
  }

  return mapSupplierListItemFromDb(data);
}

/**
 * Search suppliers by name
 */
export async function searchSuppliers(
  businessId: string,
  searchTerm: string
): Promise<SupplierListItem[]> {
  const { data, error } = await supabase
    .from('v_suppliers')
    .select('*')
    .eq('business_id', businessId)
    .ilike('name', `%${searchTerm}%`)
    .order('name', { ascending: true })
    .limit(20);

  if (error) {
    console.error('[suppliersApi] Error searching suppliers:', error);
    throw new Error(error.message);
  }

  return (data || []).map(mapSupplierListItemFromDb);
}

/**
 * Create a new supplier
 */
export async function createSupplier(input: CreateSupplierInput): Promise<Supplier> {
  const { data, error } = await supabase
    .from('suppliers')
    .insert({
      business_id: input.business_id,
      name: input.name.trim(),
      contact_email: input.contact_email || null,
      phone: input.phone || null,
      default_currency: input.default_currency || null,
      website: input.website || null,
      notes: input.notes || null,
    })
    .select()
    .single();

  if (error) {
    console.error('[suppliersApi] Error creating supplier:', error);
    throw new Error(error.message);
  }

  return mapSupplierFromDb(data);
}

/**
 * Update an existing supplier
 */
export async function updateSupplier(
  supplierId: string,
  input: UpdateSupplierInput
): Promise<Supplier> {
  // Build update object, only including defined fields
  const updateFields: Record<string, unknown> = {};

  if (input.name !== undefined) updateFields.name = input.name.trim();
  if (input.contact_email !== undefined) updateFields.contact_email = input.contact_email;
  if (input.phone !== undefined) updateFields.phone = input.phone;
  if (input.default_currency !== undefined) updateFields.default_currency = input.default_currency;
  if (input.website !== undefined) updateFields.website = input.website;
  if (input.notes !== undefined) updateFields.notes = input.notes;

  const { data, error } = await supabase
    .from('suppliers')
    .update(updateFields)
    .eq('id', supplierId)
    .select()
    .single();

  if (error) {
    console.error('[suppliersApi] Error updating supplier:', error);
    throw new Error(error.message);
  }

  return mapSupplierFromDb(data);
}

/**
 * Check if a supplier name already exists for the business
 */
export async function supplierNameExists(
  businessId: string,
  name: string,
  excludeId?: string
): Promise<boolean> {
  let query = supabase
    .from('suppliers')
    .select('id')
    .eq('business_id', businessId)
    .ilike('name', name.trim());

  if (excludeId) {
    query = query.neq('id', excludeId);
  }

  const { data, error } = await query.limit(1);

  if (error) {
    console.error('[suppliersApi] Error checking supplier name:', error);
    throw new Error(error.message);
  }

  return (data?.length ?? 0) > 0;
}
